import React, { useState, useRef } from 'react';
import { Upload, File, CheckCircle, XCircle, AlertCircle, Eye, Download, Edit, Save, X, RefreshCw, Tag, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { readJsonApiResponse } from '../lib/jsonApi';

const AdminPanel = () => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [adminFilesMetadata, setAdminFilesMetadata] = useState(null); // Metadata de admin-files.json
  const [fileDescriptions, setFileDescriptions] = useState({}); // Mapa de descripciones por archivo
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  // Helper para fetch sin cache
  const fetchNoCache = (url, options = {}) => {
    const urlWithCache = url.includes('?')
      ? `${url}&v=${Date.now()}`
      : `${url}?v=${Date.now()}`;

    return fetch(urlWithCache, {
      ...options,
      headers: {
        ...options.headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  };

  // Password simple (en producción usar algo más seguro)
  const ADMIN_PASSWORD = 'cenid2025admin';

  const handleAuth = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      loadExistingFiles();
      toast({
        title: 'Acceso autorizado',
        description: 'Bienvenido al panel de administración'
      });
    } else {
      toast({
        title: 'Error de autenticación',
        description: 'Contraseña incorrecta',
        variant: 'destructive'
      });
    }
  };

  const loadExistingFiles = async () => {
    try {
      // Cargar lista de archivos desde admin-files.json
      let knownFiles = [];
      let descriptionsMap = {};
      let metadata = null;

      try {
        // Forzar recarga completa con múltiples estrategias anti-cache
        const cacheBuster = `v=${Date.now()}&nocache=${Math.random()}`;
        
        console.log('📥 Intentando cargar admin-files.json...');
        
        const adminFilesResponse = await fetch(`/data/admin-files.json?${cacheBuster}`, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          cache: 'no-store'
        });

        console.log('📡 Respuesta admin-files.json:', {
          ok: adminFilesResponse.ok,
          status: adminFilesResponse.status,
          statusText: adminFilesResponse.statusText,
          contentType: adminFilesResponse.headers.get('content-type')
        });

        if (!adminFilesResponse.ok) {
          throw new Error(`HTTP ${adminFilesResponse.status}: ${adminFilesResponse.statusText}`);
        }

        const responseText = await adminFilesResponse.text();
        console.log('📄 Contenido recibido (primeros 200 chars):', responseText.substring(0, 200));

        // Verificar que no sea HTML
        if (responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')) {
          throw new Error('El servidor devolvió HTML en lugar de JSON para admin-files.json');
        }

        const adminFilesData = JSON.parse(responseText);
        console.log('✅ admin-files.json parseado correctamente:', adminFilesData);

        console.log(`📥 admin-files.json cargado - Versión: ${adminFilesData.metadata?.version || 'N/A'}`);

        // Verificar estructura
        if (!adminFilesData.archivos || !Array.isArray(adminFilesData.archivos)) {
          throw new Error('admin-files.json no tiene la estructura esperada (falta array "archivos")');
        }

        // Guardar metadata del sistema
        metadata = adminFilesData.metadata;
        setAdminFilesMetadata(metadata);
        console.log('✅ Metadata guardada:', metadata);

        // Crear mapa de descripciones y metadata por archivo
        let processedCount = 0;
        adminFilesData.archivos.forEach(archivo => {
          if (archivo.editable) {
            descriptionsMap[archivo.nombre] = {
              descripcion: archivo.descripcion,
              categoria: archivo.categoria,
              critico: archivo.critico || false
            };
            processedCount++;
          }
        });
        
        // ✨ AGREGAR admin-files.json a las descripciones para que sea editable
        descriptionsMap['admin-files.json'] = {
          descripcion: 'Configuración de archivos editables del AdminPanel',
          categoria: 'Configuración General',
          critico: true
        };
        
        setFileDescriptions(descriptionsMap);
        console.log(`✅ Descripciones procesadas: ${processedCount} archivos + admin-files.json`);
        console.log('📋 Mapa de descripciones:', descriptionsMap);

        // Extraer solo los nombres de archivos editables
        knownFiles = adminFilesData.archivos
          .filter(f => f.editable)
          .map(f => f.nombre);
      
      // ✨ AGREGAR admin-files.json a la lista de archivos conocidos
      if (!knownFiles.includes('admin-files.json')) {
        knownFiles.push('admin-files.json');
      }
      
      console.log(`✅ Cargados ${knownFiles.length} archivos desde admin-files.json (incluyendo admin-files.json)`);
      console.log('📋 Archivos editables:', knownFiles);

    } catch (adminError) {
      console.error('❌ Error cargando admin-files.json:', adminError);
      console.error('Stack:', adminError.stack);
      
      toast({
        title: '⚠️ Error cargando configuración',
        description: `No se pudo cargar admin-files.json: ${adminError.message}. Usando lista de respaldo.`,
        variant: 'destructive',
        duration: 5000
      });

      // Lista de respaldo en caso de error
      knownFiles = [
        'admin-files.json', // ← Incluir en respaldo también
        'carousel.json',
        'catedra-home.json',
        'configuracion.json',
        'congresos-lista.json',
        'conversatorio.json',
        'doctorado.json',
        'estancias.json',
        'generales.json',
        'libros.json',
        'maestria.json',
        'menu.json',
        'postdoctorados.json',
        'redes-estadisticas-fechas.json',
        'redriel.json',
        'redunadi.json',
        'revistas.json',
        'seminarios.json',
        'unitwin.json',
        'upeace.json'
      ];
      console.log('⚠️ Usando lista de respaldo con', knownFiles.length, 'archivos');
    }

    // Detectar si estamos en desarrollo o producción
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.port === '3000' ||
                         window.location.port === '5173';

    console.log(`🌍 Entorno detectado: ${isDevelopment ? 'DESARROLLO' : 'PRODUCCIÓN'}`);
    console.log(`📍 Hostname: ${window.location.hostname}, Port: ${window.location.port}`);

    // ESTRATEGIA: Intentar API PHP primero, luego fetch directo
    let useDirectFetch = isDevelopment;
    
    if (!isDevelopment) {
      // En producción, intentar API PHP primero
      try {
        console.log('🔄 [PRODUCCIÓN] Intentando cargar archivos desde API PHP...');
        const apiResponse = await fetchNoCache('/api/json-manager.php?action=list');
        
        console.log('📡 Respuesta de API:', {
          ok: apiResponse.ok,
          status: apiResponse.status,
          statusText: apiResponse.statusText
        });
        
        if (!apiResponse.ok) {
          throw new Error(`HTTP ${apiResponse.status}: ${apiResponse.statusText}`);
        }
        
        const apiData = await apiResponse.json();
        
        console.log('📊 Datos recibidos de la API:', apiData);
        
        if (apiData.success && apiData.files && Array.isArray(apiData.files)) {
          console.log(`✅ API PHP respondió correctamente con ${apiData.files.length} archivos`);
          
          // Filtrar solo los archivos que están en admin-files.json (editables)
          const filteredFiles = knownFiles.length > 0 
            ? apiData.files.filter(file => knownFiles.includes(file.name))
            : apiData.files;
          
          console.log(`📋 Archivos después del filtro: ${filteredFiles.length}`);
          
          // Enriquecer con metadata de admin-files.json
          const enrichedFiles = filteredFiles.map(file => {
            const metadata = descriptionsMap[file.name] || {};
            console.log(`📝 Enriqueciendo ${file.name}:`, metadata);
            return {
              ...file,
              ...metadata,
              status: 'success'
            };
          });
          
          console.log('✨ Archivos enriquecidos:', enrichedFiles);
          
          setExistingFiles(enrichedFiles);
          
          toast({
            title: '✅ Archivos cargados',
            description: `${enrichedFiles.length} archivos disponibles`,
            duration: 3000
          });
          
          console.log('✅ Archivos cargados exitosamente desde API PHP');
          return; // ← ÉXITO: salir aquí
        } else {
          console.warn('⚠️ API PHP respondió pero sin datos válidos:', apiData);
          throw new Error(apiData.error || 'API no devolvió datos válidos');
        }
      } catch (apiError) {
        console.error('❌ Error llamando a API PHP:', apiError.message);
        console.warn('⚠️ Intentando fallback con fetch directo...');
        useDirectFetch = true;
      }
    }

    // FALLBACK o DESARROLLO: Usar fetch directo
    if (useDirectFetch) {
      console.log(`🔄 [${isDevelopment ? 'DESARROLLO' : 'FALLBACK'}] Cargando archivos con fetch directo...`);
      
      const files = await Promise.all(
        knownFiles.map(async (filename) => {
          try {
            const attempts = [
              `/data/${filename}?v=${Date.now()}`,
              `${window.location.origin}/data/${filename}?v=${Date.now()}`
            ];

            let response;
            let lastError = null;

            for (const url of attempts) {
              try {
                response = await fetchNoCache(url);
                if (response.ok) break;
                lastError = `HTTP ${response.status} for ${url}`;
              } catch (e) {
                lastError = `Network error for ${url}: ${e.message}`;
                continue;
              }
            }

            if (!response || !response.ok) {
              throw new Error(lastError || 'All fetch attempts failed');
            }

            const text = await response.text();

            if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
              throw new Error('Server returned HTML instead of JSON');
            }

            const data = JSON.parse(text);

            const metadata = descriptionsMap[filename] || {};
            
            return {
              name: filename,
              size: text.length,
              lastModified: response.headers.get('last-modified') || new Date().toLocaleDateString(),
              status: 'success',
              ...metadata
            };
          } catch (error) {
            console.error(`Error loading ${filename}:`, error.message);
            const metadata = descriptionsMap[filename] || {};
            return {
              name: filename,
              status: 'error',
              error: error.message.substring(0, 150),
              size: 0,
              lastModified: 'N/A',
              ...metadata
            };
          }
        })
      );

      setExistingFiles(files);
      
      const successCount = files.filter(f => f.status === 'success').length;
      const errorCount = files.filter(f => f.status === 'error').length;
      
      toast({
        title: successCount > 0 ? '✅ Archivos cargados' : '⚠️ Algunos archivos no se pudieron cargar',
        description: `${successCount} correctos, ${errorCount} con errores`,
        variant: errorCount > 0 ? 'destructive' : 'default',
        duration: 3000
      });
      
      console.log(`✅ Archivos cargados con fetch directo: ${successCount} correctos, ${errorCount} errores`);
    }

  } catch (error) {
    console.error('❌ Error crítico en loadExistingFiles:', error);
    console.error('Stack:', error.stack);
    
    toast({
      title: 'Error cargando archivos',
      description: error.message,
      variant: 'destructive'
    });
  }
};

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const jsonFiles = fileArray.filter(file =>
      file.type === 'application/json' || file.name.endsWith('.json')
    );

    if (jsonFiles.length === 0) {
      toast({
        title: 'Error',
        description: 'Solo se permiten archivos JSON',
        variant: 'destructive'
      });
      return;
    }

    uploadFiles(jsonFiles);
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    const results = [];

    for (const file of files) {
      try {
        // Validar JSON
        const text = await file.text();
        JSON.parse(text); // Esto lanzará error si no es JSON válido

        const formData = new FormData();
        formData.append('jsonFile', file);

        const response = await fetch('/api/upload-json.php', {
          method: 'POST',
          body: formData
        });

        const result = await readJsonApiResponse(response);

        results.push({
          filename: file.name,
          success: response.ok && result.success === true,
          message: result.error || result.message || 'Respuesta de guardado incompleta',
          size: file.size
        });

      } catch (error) {
        results.push({
          filename: file.name,
          success: false,
          message: error.message || 'Error al procesar archivo',
          size: file.size
        });
      }
    }

    setUploadResults(results);
    setUploading(false);

    if (results.some(r => r.success)) {
      loadExistingFiles(); // Recargar lista
      toast({
        title: 'Subida completada',
        description: `${results.filter(r => r.success).length} archivo(s) actualizado(s)`
      });
    }
  };

  const viewFileContent = async (filename) => {
    try {
      // Intentar primero con la API PHP
      try {
        const apiResponse = await fetchNoCache(`/api/json-manager.php?action=get&file=${encodeURIComponent(filename)}`);
        if (apiResponse.ok) {
          const apiData = await apiResponse.json();
          if (apiData.content) {
            console.log('✅ Usando API PHP para obtener contenido');
            setSelectedFile({
              name: filename,
              content: JSON.stringify(apiData.content, null, 2),
              rawContent: JSON.stringify(apiData.content),
              url: 'API PHP',
              size: apiData.size,
              lastModified: apiData.lastModified
            });
            return;
          }
        }
      } catch (apiError) {
        console.warn('API PHP falló, intentando fetch directo:', apiError.message);
      }

      // Fallback: intentar múltiples URLs para encontrar el archivo
      const attempts = [
        `/data/${filename}?v=${Date.now()}`,
        `${window.location.origin}/data/${filename}?v=${Date.now()}`,
        `/public/data/${filename}?v=${Date.now()}`
      ];

      let response;
      let lastError = null;

      for (const url of attempts) {
        try {
          response = await fetchNoCache(url);
          if (response.ok) {
            break;
          } else {
            lastError = `HTTP ${response.status}: ${response.statusText} for ${url}`;
          }
        } catch (e) {
          lastError = `Network error for ${url}: ${e.message}`;
          continue;
        }
      } if (!response || !response.ok) {
        throw new Error(lastError || 'No se pudo acceder al archivo desde ninguna URL');
      }

      const text = await response.text();

      // Verificar si el contenido parece HTML (error de servidor)
      if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
        throw new Error('El servidor devolvió HTML en lugar de JSON. El archivo puede no existir o hay un problema de configuración del servidor.');
      }

      // Intentar parsear como JSON
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        throw new Error(`JSON inválido: ${parseError.message}. Contenido: ${text.substring(0, 100)}...`);
      }

      setSelectedFile({
        name: filename,
        content: JSON.stringify(data, null, 2),
        rawContent: text,
        url: response.url
      });
    } catch (error) {
      console.error('Error loading file content:', error);
      toast({
        title: 'Error cargando archivo',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const downloadFile = (filename) => {
    const link = document.createElement('a');
    link.href = `/data/${filename}`;
    link.download = filename;
    link.click();
  };

  const editFile = async (filename) => {
    try {
      console.log('=== CARGANDO ARCHIVO PARA EDITAR ===');
      console.log('Archivo:', filename);

      toast({
        title: 'Cargando archivo...',
        description: `Abriendo ${filename}`
      });


      const response = await fetchNoCache(`/data/${filename}`);
      console.log('Respuesta:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: No se pudo cargar el archivo`);
      }

      const text = await response.text();
      console.log('Contenido cargado:', text.length, 'caracteres');

      const data = JSON.parse(text);
      const formatted = JSON.stringify(data, null, 2);

      console.log('JSON parseado y formateado correctamente');

      setEditingFile({
        name: filename,
        originalContent: formatted
      });
      setEditContent(formatted);
      setJsonError(null);

      console.log('✅ Archivo listo para editar');
    } catch (error) {
      console.error('❌ Error cargando archivo:', error);
      toast({
        title: 'Error cargando archivo',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const validateAndFormatJSON = (text) => {
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      return { valid: true, formatted: JSON.stringify(parsed, null, 2), parsed };
    } catch (error) {
      setJsonError(error.message);
      return { valid: false, error: error.message };
    }
  };

  const handleEditorChange = (e) => {
    const newContent = e.target.value;
    setEditContent(newContent);
    validateAndFormatJSON(newContent);
  };

  const formatJSON = () => {
    const result = validateAndFormatJSON(editContent);
    if (result.valid) {
      setEditContent(result.formatted);
      toast({
        title: 'JSON formateado',
        description: 'El código ha sido formateado correctamente'
      });
    }
  };

  const saveEditedFile = async () => {
    const validation = validateAndFormatJSON(editContent);
    if (!validation.valid) {
      toast({
        title: 'JSON inválido',
        description: 'Por favor corrige los errores antes de guardar',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    console.log('=== INICIANDO GUARDADO ===');
    console.log('Archivo:', editingFile.name);
    console.log('Tamaño del contenido:', editContent.length);

    try {
      // Crear un Blob con el contenido JSON
      const blob = new Blob([editContent], { type: 'application/json' });
      console.log('Blob creado:', blob.size, 'bytes');

      // Usar FormData y agregar el blob directamente con nombre de archivo
      const formData = new FormData();
      formData.append('jsonFile', blob, editingFile.name);

      console.log('FormData preparado con blob, enviando a /api/upload-json.php');

      const response = await fetch('/api/upload-json.php', {
        method: 'POST',
        body: formData
      });

      console.log('Respuesta recibida:', response.status, response.statusText);

      const contentType = response.headers.get('content-type');
      console.log('Content-Type de respuesta:', contentType);

      const result = await readJsonApiResponse(response);

      if (response.ok && result.success) {
        console.log('✅ Guardado exitoso');
        toast({
          title: 'Archivo guardado',
          description: `${editingFile.name} actualizado exitosamente`
        });
        setEditingFile(null);
        setEditContent('');
        setJsonError(null);

        // Esperar un momento antes de recargar
        setTimeout(() => {
          console.log('Recargando lista de archivos...');
          loadExistingFiles();
        }, 500);
      } else {
        console.error('❌ Error en respuesta:', result);
        throw new Error(result.error || result.message || 'Error desconocido al guardar');
      }
    } catch (error) {
      console.error('❌ Error en saveEditedFile:', error);
      toast({
        title: 'Error al guardar',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
      console.log('=== FIN GUARDADO ===');
    }
  };

  const cancelEdit = () => {
    setEditingFile(null);
    setEditContent('');
    setJsonError(null);
  };

  const incrementVersion = (version) => {
    const parts = version.split('.');
    const patch = parseInt(parts[2] || '0', 10);
    return `${parts[0]}.${parts[1]}.${patch + 1}`;
  };

  const updateAdminFilesMetadata = async () => {
    try {
      console.log('🔄 Actualizando versión de admin-files.json...');

      // Cargar admin-files.json actual con cache busting agresivo
      const cacheBuster = `v=${Date.now()}&nocache=${Math.random()}`;
      const response = await fetch(`/data/admin-files.json?${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });

      const adminFilesData = await response.json();

      // Incrementar versión
      const oldVersion = adminFilesData.metadata.version;
      const newVersion = incrementVersion(oldVersion);

      // Actualizar metadata
      adminFilesData.metadata.version = newVersion;
      adminFilesData.metadata.ultimaActualizacion = new Date().toISOString().split('T')[0];

      console.log(`📝 Versión: ${oldVersion} → ${newVersion}`);

      // Guardar archivo actualizado
      const blob = new Blob([JSON.stringify(adminFilesData, null, 2)], { type: 'application/json' });
      const formData = new FormData();
      formData.append('jsonFile', blob, 'admin-files.json');

      const uploadResponse = await fetch('/api/upload-json.php', {
        method: 'POST',
        body: formData
      });

      const result = await readJsonApiResponse(uploadResponse);

      if (result.success) {
        // Actualizar estado inmediatamente
        setAdminFilesMetadata(adminFilesData.metadata);

        console.log('✅ Versión actualizada correctamente');

        toast({
          title: '✅ Versión actualizada',
          description: `${oldVersion} → ${newVersion}`,
          duration: 3000
        });

        // Recargar lista de archivos después de un momento
        setTimeout(() => {
          console.log('🔄 Recargando lista de archivos...');
          loadExistingFiles();
        }, 500);
      } else {
        throw new Error(result.error || 'Error al actualizar');
      }
    } catch (error) {
      console.error('❌ Error al actualizar versión:', error);
      toast({
        title: '❌ Error al actualizar versión',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const testUploadAPI = async () => {
    console.log('=== TEST DE API DE SUBIDA ===');

    try {
      // Crear un archivo JSON de prueba simple
      const testData = { test: true, timestamp: new Date().toISOString() };
      const testContent = JSON.stringify(testData, null, 2);
      const blob = new Blob([testContent], { type: 'application/json' });

      console.log('Blob de prueba creado:', blob.size, 'bytes');

      const formData = new FormData();
      formData.append('jsonFile', blob, 'test-upload.json');

      console.log('Enviando a /api/upload-json.php...');

      const response = await fetch('/api/upload-json.php', {
        method: 'POST',
        body: formData
      });

      console.log('Respuesta:', response.status, response.statusText);
      console.log('Headers:', [...response.headers.entries()]);

      const result = await readJsonApiResponse(response);
      console.log('Resultado parseado:', result);

      if (result.success) {
        toast({
          title: '✅ API funcionando correctamente',
          description: 'La subida de archivos está operativa'
        });
      } else {
        toast({
          title: '⚠️ API responde pero con error',
          description: result.error || 'Error desconocido',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('❌ Error en test:', error);
      toast({
        title: '❌ Error en API',
        description: error.message,
        variant: 'destructive'
      });
    }

    console.log('=== FIN TEST ===');
  };

  const checkPermissions = async () => {
    console.log('=== VERIFICANDO PERMISOS ===');

    try {
      const response = await fetchNoCache('/api/check-permissions.php');
      const data = await response.json();

      console.log('Información del servidor:', data);      // Verificar problemas críticos
      const issues = [];
      if (!data.data_dir.exists) issues.push('❌ Directorio /data/ no existe');
      if (!data.data_dir.writable) issues.push('❌ Directorio /data/ no tiene permisos de escritura');
      if (!data.backup_dir.exists) issues.push('⚠️ Directorio /data/backups/ no existe');
      if (!data.backup_dir.writable) issues.push('⚠️ Directorio /data/backups/ no tiene permisos de escritura');
      if (!data.write_test.success) issues.push('❌ Test de escritura falló');

      if (issues.length > 0) {
        toast({
          title: 'Problemas detectados',
          description: issues.join('\n'),
          variant: 'destructive'
        });
      } else {
        toast({
          title: '✅ Todo correcto',
          description: 'Permisos y directorios configurados correctamente'
        });
      }

      console.table({
        'Directorio data': data.data_dir.writable ? '✅ Escribible' : '❌ No escribible',
        'Directorio backups': data.backup_dir.writable ? '✅ Escribible' : '❌ No escribible',
        'Test escritura': data.write_test.success ? '✅ OK' : '❌ Falló'
      });

    } catch (error) {
      console.error('❌ Error verificando permisos:', error);
      toast({
        title: 'Error de diagnóstico',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const testProductionAPI = async () => {
    console.log('=== 🔍 DIAGNÓSTICO COMPLETO DE API ===');
    
    try {
      // Test 1: API de diagnóstico básica
      console.log('1️⃣ Probando API de diagnóstico...');
      const testResponse = await fetchNoCache('/api/test-api.php');
      
      console.log('Status:', testResponse.status, testResponse.statusText);
      console.log('Headers:', Object.fromEntries(testResponse.headers.entries()));
      
      if (!testResponse.ok) {
        throw new Error(`HTTP ${testResponse.status}: ${testResponse.statusText}`);
      }
      
      const testData = await testResponse.json();
      console.log('📊 Respuesta del servidor:', testData);
      
      // Mostrar tabla de diagnóstico
      console.table({
        'PHP Versión': testData.server_info?.php_version || 'N/A',
        'Servidor': testData.server_info?.server_software || 'N/A',
        'Dir data existe': testData.permissions?.data_dir_exists ? '✅' : '❌',
        'Dir data legible': testData.permissions?.data_dir_readable ? '✅' : '❌',
        'Dir data escribible': testData.permissions?.data_dir_writable ? '✅' : '❌',
        'json-manager existe': testData.files?.json_manager_exists ? '✅' : '❌',
        'Archivos JSON encontrados': testData.total_json_files || 0
      });
      
      // Test 2: API de gestión de archivos
      console.log('\n2️⃣ Probando API de gestión (json-manager.php)...');
      const listResponse = await fetchNoCache('/api/json-manager.php?action=list');
      
      console.log('Status:', listResponse.status, listResponse.statusText);
      
      if (!listResponse.ok) {
        throw new Error(`json-manager.php respondió con HTTP ${listResponse.status}`);
      }
      
      const listData = await listResponse.json();
      console.log('📋 Respuesta de listado:', listData);
      
      if (listData.success && listData.files) {
        console.log(`✅ json-manager.php funciona: ${listData.files.length} archivos`);
        console.table(listData.files.slice(0, 5)); // Primeros 5 archivos
        
        toast({
          title: '✅ API funcionando correctamente',
          description: `PHP OK | ${testData.total_json_files} archivos encontrados | json-manager.php operativo`,
          duration: 5000
        });
        
        // Intentar recargar archivos automáticamente
        console.log('\n3️⃣ Recargando lista de archivos...');
        await loadExistingFiles();
        
      } else {
        throw new Error(listData.error || 'json-manager.php no devolvió datos válidos');
      }
      
    } catch (error) {
      console.error('❌ Error en diagnóstico:', error);
      console.error('Stack:', error.stack);
      
      toast({
        title: '❌ Error de API',
        description: error.message,
        variant: 'destructive',
        duration: 10000
      });
      
      // Mostrar soluciones sugeridas
      console.log('\n🔧 SOLUCIONES SUGERIDAS:');
      console.log('1. Verificar que /api/test-api.php existe en el servidor');
      console.log('2. Verificar permisos: chmod 755 /var/www/cenid.tinoco.mx/2025/frontend/public/api/');
      console.log('3. Verificar permisos: chmod 755 /var/www/cenid.tinoco.mx/2025/frontend/public/data/');
      console.log('4. Verificar logs de Apache/Nginx: tail -f /var/log/apache2/error.log');
      console.log('5. Probar directamente: curl https://cenid.org/api/test-api.php');
    }
    
    console.log('=== FIN DIAGNÓSTICO ===\n');
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h1 className="text-2xl font-bold text-center mb-6">Panel de Administración</h1>
          <form onSubmit={handleAuth}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Contraseña de acceso
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0b742f]"
                placeholder="Ingrese la contraseña"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-[#0b742f] text-white py-2 px-4 rounded-lg hover:brightness-95 transition-all"
            >
              Acceder
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Panel de Administración JSON</h1>
                <p className="text-gray-600 mt-2">Subir y gestionar archivos de configuración</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    console.log('=== DIAGNÓSTICO ===');
                    console.log('Location:', window.location.href);
                    console.log('Hostname:', window.location.hostname);
                    console.log('Port:', window.location.port);
                    console.log('Protocol:', window.location.protocol);
                    console.log('Base URL attempts will be:', [
                      `/data/menu.json?v=${Date.now()}`,
                      `${window.location.origin}/data/menu.json?v=${Date.now()}`,
                      `/public/data/menu.json?v=${Date.now()}`
                    ]);

                    // Test fetch inmediato
                    fetchNoCache('/data/menu.json')
                      .then(r => {
                        console.log('Test fetch status:', r.status, r.statusText);
                        console.log('Content-Type:', r.headers.get('content-type'));
                        return r.text();
                      })
                      .then(text => {
                        console.log('First 200 chars:', text.substring(0, 200));
                        console.log('Is HTML?', text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html'));
                      })
                      .catch(e => console.log('Test fetch error:', e.message));
                  }}
                  className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded hover:bg-gray-200"
                >
                  Debug Info
                </button>
                <button
                  onClick={testUploadAPI}
                  className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded hover:bg-blue-200 ml-2"
                >
                  Test API Subida
                </button>
                <button
                  onClick={checkPermissions}
                  className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded hover:bg-green-200 ml-2"
                >
                  Verificar Permisos
                </button>
                <button
                  onClick={testProductionAPI}
                  className="text-xs bg-purple-100 text-purple-600 px-3 py-1 rounded hover:bg-purple-200 ml-2 font-medium"
                >
                  🔍 Diagnóstico Completo
                </button>
              </div>
            </div>
          </div>

          {/* Admin Files Metadata Section */}
          {adminFilesMetadata && (
            <div className="p-6 border-b border-gray-200 bg-blue-50">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    Configuración del Sistema
                  </h2>
                  <div className="flex gap-4 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Versión:</span>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-mono">
                        {adminFilesMetadata.version}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Última actualización:</span>
                      <span className="text-gray-600">
                        {new Date(adminFilesMetadata.ultimaActualizacion).toLocaleDateString('es-MX', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={updateAdminFilesMetadata}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Incrementar Versión
                </button>
              </div>
            </div>
          )}

          {/* Upload Area */}
          <div className="p-6">
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                ? 'border-[#0b742f] bg-green-50'
                : 'border-gray-300 hover:border-gray-400'
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".json,application/json"
                onChange={(e) => handleFiles(e.target.files)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />

              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Arrastra archivos JSON aquí
              </p>
              <p className="text-gray-600 mb-4">
                o haz clic para seleccionar archivos
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-[#0b742f] text-white px-6 py-2 rounded-lg hover:brightness-95 transition-all"
                disabled={uploading}
              >
                {uploading ? 'Subiendo...' : 'Seleccionar archivos'}
              </button>
            </div>
          </div>
        </div>

        {/* Upload Results */}
        {uploadResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Resultados de subida</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {uploadResults.map((result, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {result.success ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{result.filename}</p>
                      <p className="text-sm text-gray-600">{result.message}</p>
                    </div>
                    <span className="text-sm text-gray-500">
                      {(result.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Existing Files */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Archivos existentes</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {existingFiles.map((file, index) => {
                const fileInfo = fileDescriptions[file.name] || {};
                return (
                  <div key={index} className={`border rounded-lg p-4 ${file.status === 'success' ? 'border-gray-200' :
                    file.status === 'error' ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'
                    }`}>
                    <div className="flex items-start gap-3">
                      {file.status === 'success' ? (
                        <File className="w-5 h-5 text-[#0b742f] mt-0.5" />
                      ) : file.status === 'error' ? (
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-medium">{file.name}</h3>
                          {fileInfo.critico && (
                            <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" title="Archivo crítico" />
                          )}
                        </div>

                        {fileInfo.categoria && (
                          <div className="mt-1">
                            <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              <Tag className="w-3 h-3" />
                              {fileInfo.categoria}
                            </span>
                          </div>
                        )}

                        {fileInfo.descripcion && (
                          <p className="text-sm text-gray-600 mt-2">
                            {fileInfo.descripcion}
                          </p>
                        )}

                        {file.status === 'success' ? (
                          <p className="text-sm text-gray-500 mt-2">
                            {(file.size / 1024).toFixed(1)} KB
                            {file.contentType && (
                              <span className="ml-2 text-xs text-gray-400">
                                ({file.contentType})
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="text-sm text-red-600 mt-2">
                            {file.error || 'Error desconocido'}
                          </p>
                        )}
                      </div>
                    </div>
                    {file.status === 'success' && (
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => editFile(file.name)}
                          className="flex items-center gap-1 text-sm text-[#0b742f] hover:text-[#e87729] font-medium"
                        >
                          <Edit className="w-4 h-4" />
                          Editar
                        </button>
                        <button
                          onClick={() => viewFileContent(file.name)}
                          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                        >
                          <Eye className="w-4 h-4" />
                          Ver
                        </button>
                        <button
                          onClick={() => downloadFile(file.name)}
                          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                        >
                          <Download className="w-4 h-4" />
                          Descargar
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* File Viewer Modal */}
        {selectedFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold">{selectedFile.name}</h3>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4 overflow-auto max-h-[60vh]">
                <pre className="text-sm bg-gray-100 p-4 rounded-lg overflow-auto">
                  {selectedFile.content}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* JSON Editor Modal */}
        {editingFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold">Editando: {editingFile.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Los cambios se guardarán automáticamente con backup
                  </p>
                </div>
                <button
                  onClick={cancelEdit}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Editor Area */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex-1 overflow-auto p-4">
                  <textarea
                    value={editContent}
                    onChange={handleEditorChange}
                    className="w-full h-full min-h-[500px] p-4 font-mono text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0b742f] resize-none"
                    spellCheck="false"
                    style={{
                      tabSize: 2,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}
                  />
                </div>

                {/* Error Display */}
                {jsonError && (
                  <div className="px-4 py-3 bg-red-50 border-t border-red-200">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Error en JSON:</p>
                        <p className="text-sm text-red-700 font-mono">{jsonError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Valid JSON Indicator */}
                {!jsonError && editContent && (
                  <div className="px-4 py-3 bg-green-50 border-t border-green-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <p className="text-sm text-green-800">JSON válido ✓</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                <button
                  onClick={formatJSON}
                  disabled={!!jsonError}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Formatear JSON
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={cancelEdit}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={saveEditedFile}
                    disabled={!!jsonError || saving}
                    className="flex items-center gap-2 px-6 py-2 bg-[#0b742f] text-white rounded-lg hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Guardar cambios
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
